import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Icon } from '../../ui/Icon';

export interface ScopeTab {
  /** 'mine' | 'common' | a member's userId. */
  key: string;
  label: string;
  count: number;
}

interface Props {
  tabs: ScopeTab[];
  scope: string;
  onScopeChange: (scope: string) => void;
  sharesPacking: boolean;
  onToggleShare: () => void;
  /** The toggle governs YOUR list, so it only belongs next to your list. */
  showSharing: boolean;
}

/**
 * Visible is not the same as mixed.
 *
 * Sharing a packing list with seven other people is fine — what is unbearable
 * is forty of their sections on your screen. So the tabs are not a permission
 * control, they are the answer to "whose list am I looking at": the filter is
 * what makes sharing-by-default liveable in a group.
 */
export default function PackingScopeBar({
  tabs, scope, onScopeChange, sharesPacking, onToggleShare, showSharing,
}: Props) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
      <div className="flex items-center gap-1.5 flex-wrap" role="tablist">
        {tabs.map(tab => {
          const active = tab.key === scope;
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={active}
              onClick={() => onScopeChange(tab.key)}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors cursor-pointer ${
                active
                  ? 'bg-teal-500 text-white'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`tabular-nums ${active ? 'text-white/70' : 'text-slate-400 dark:text-slate-500'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Sat next to the shared tab it read as "the group can see THIS", which
          is not what it does — it says whether the others can see YOUR list. */}
      {showSharing && (
        <button
          onClick={onToggleShare}
          className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
          title={sharesPacking ? t('packing.sharing.onHint') : t('packing.sharing.offHint')}
        >
          <Icon as={sharesPacking ? Eye : EyeOff} size={13} />
          {sharesPacking ? t('packing.sharing.on') : t('packing.sharing.off')}
        </button>
      )}
    </div>
  );
}
